// biome-ignore-all lint/suspicious/noArrayIndexKey: the comment thread is a document-order list that is never reordered mid-render; index is the stable identity here.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: note text renders through plainTextDisplayHtml, which HTML-escapes the entire comment before adding <br>/spacer markup — user input is never interpreted as HTML.
// =============================================================================
// CommentPanel.jsx
// =============================================================================
// The comments slide-in modal used by both shelter users and staff/responders
// inside ReqDetail. Extracted from RequestDetail.jsx (Phase D).
//
// Props
// ─────
//   req                  — request object (id, refNo, shelterEmail, shelterName, shelterCode, state)
//   user                 — current user (shelterName)
//   isZephyr              — THE FENCE (shelter vs Zephyr). True for ANY internal Zephyr
//                          viewer (responder, admin, OR a picker browsing All Requests),
//                          false only for a shelter caseworker. Drives who a comment is
//                          authored as (byRole/tier), whether it notifies the shelter and
//                          routes as a Zephyr reply (email) vs a shelter comment (task),
//                          and whether internal notes + the compose tools show. A Zephyr
//                          viewer therefore never authors as, or is treated as, the shelter.
//   notes                — saved comment array
//   setNotes             — update saved comments
//   note                 — current textarea value
//   setNote              — update textarea
//   emailInvoicing       — "also email invoicing" checkbox
//   setEmailInvoicing    — update checkbox
//   onClose              — called to close the panel (resets note + emailInvoicing)
//   setShowTemplatePicker — opens the template picker modal
//
// Two compose modes for a Zephyr viewer (David, 2 Nov 2026): the "Comment to DFV
// Service" tab (teal — the shelter sees it and is emailed) and the "Internal Note"
// tab (slate — saved to request_internal_notes, db-122; never shown to the DFV
// Service, no email, no unread flag, no task). Responders were writing request-only
// notes into the DFV Service's GLOBAL notes because there was nowhere else hidden.
// The panel always opens on the Comment tab; the typed text is kept across a switch.
// A shelter viewer sees no tabs — the single Add Comment, as before.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { isAdminRole } from "../../app/access";
import { insertInternalNote, listInternalNotesByRequest } from "../../data/requestInternalNotes";
import { appendRequestNotes } from "../../data/requests";
import { alertDialog } from "../../services/dialogService";
import { plainTextDisplayHtml } from "../../services/richText";
import { createTask, sendEmail } from "../../utils/actions";
import { logAudit } from "../../utils/auditLog";
import { authorCommentTier, C, commentTierColours } from "../../utils/constants";
import { GLYPH } from "../../utils/glyphs";
import { formatRef } from "../../utils/helpers";
import { sanitizeNote, sanitizeText } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

// Compose-box colours per mode. Comment = the palette teal (index.css tokens via C).
// Internal = the same slate literals the saved internal-note cards already use in the
// thread below (#ECEFF1 / #455A64), so "grey = hidden from the DFV Service" reads
// consistently; there is no palette token for that slate yet.
const NOTE_THEMES = {
  comment: { bg: C.tealLight, border: C.tealDark, text: C.text },
  internal: { bg: "#ECEFF1", border: "#455A64", text: "#263238" },
};

export function CommentPanel({
  req,
  user,
  isZephyr,
  notes,
  setNotes,
  note,
  setNote,
  emailInvoicing,
  setEmailInvoicing,
  onClose,
  setShowTemplatePicker,
  onActivity,
}) {
  // The teal header (below) is the drag handle — the modal opens centred and can
  // be moved by dragging it (buttons inside the header still click normally).
  const headerRef = useRef(null);

  // Staff-only INTERNAL notes (db-122) — e.g. the reason given when assigning a
  // request's tasks. RLS makes this an empty list for shelter users, but we also
  // gate the fetch on isZephyr so it's never even requested on the shelter side.
  const [internalNotes, setInternalNotes] = useState([]);
  useEffect(() => {
    if (!isZephyr || !req?.id) {
      setInternalNotes([]);
      return;
    }
    let alive = true;
    listInternalNotesByRequest(req.id).then((rows) => {
      if (alive && rows) setInternalNotes(rows);
    });
    return () => {
      alive = false;
    };
  }, [isZephyr, req?.id]);

  // Compose mode (Zephyr only): "comment" → shelter-visible comment; "internal" →
  // staff-only internal note. Always starts on "comment" (the panel remounts on open).
  const [mode, setMode] = useState("comment");
  const internalMode = isZephyr && mode === "internal";
  const [savingInternal, setSavingInternal] = useState(false);
  const theme = internalMode ? NOTE_THEMES.internal : NOTE_THEMES.comment;

  // Save a staff-only internal note for THIS request (db-122). Unlike Add Comment it
  // sends no email, does not mark the request unread for the DFV Service, and creates
  // no task. On failure the typed text is kept so nothing is lost.
  async function addInternalNote() {
    if (!note.trim() || savingInternal) return;
    const author = sanitizeText(user.shelterName);
    const text = sanitizeNote(note);
    setSavingInternal(true);
    const { error } = await insertInternalNote({
      requestId: req.id,
      author,
      authorId: user.id || null,
      text,
      kind: "note",
    });
    setSavingInternal(false);
    if (error) {
      await alertDialog({
        title: "Internal note not saved",
        message:
          "The internal note could not be saved. Your text is still in the box — please try again.",
      });
      return;
    }
    setNote("");
    logAudit("internal_note_added", { request_id: req.id, ref: req.refNo, by: author }, req.id);
    // Same as a staff comment: responder activity that does not clear the task, so the
    // exit prompt can offer to mark it actioned.
    if (onActivity) onActivity();
    // Re-read so the new note shows with its real id/timestamp; if that read fails,
    // show it locally so the responder still sees it landed.
    const rows = await listInternalNotesByRequest(req.id);
    if (rows) {
      setInternalNotes(rows);
    } else {
      setInternalNotes((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          request_id: req.id,
          author,
          author_id: user.id || null,
          text,
          kind: "note",
          created_at: new Date().toISOString(),
        },
      ]);
    }
  }

  // The rendered thread: shelter comments + (staff only) internal notes, merged in
  // date order. Comments prefer the ISO `ts`; legacy ones fall back to parsing the
  // locale `date`. Internal notes sort by created_at. Newest-first at render time.
  const thread = useMemo(() => {
    const comments = notes.map((n, idx) => ({
      kind: "comment",
      key: `c${idx}`,
      author: n.author,
      text: n.text,
      // Render from the UTC `ts` in the VIEWER's local time, so a volunteer logging
      // in from overseas sees their own clock — not the author's frozen local string.
      // Falls back to the baked `date` for legacy notes written before `ts` existed.
      date: n.ts ? new Date(n.ts).toLocaleString("en-AU") : n.date,
      scheme: commentTierColours(n),
      sortTs: n.ts ? Date.parse(n.ts) : n.date ? Date.parse(n.date) || 0 : 0,
    }));
    const internal = (isZephyr ? internalNotes : []).map((r) => ({
      kind: "internal",
      key: `i${r.id}`,
      author: r.author,
      text: r.text,
      date: new Date(r.created_at).toLocaleString("en-AU"),
      sortTs: Date.parse(r.created_at) || 0,
    }));
    return [...comments, ...internal].sort((a, b) => a.sortTs - b.sortTs);
  }, [notes, internalNotes, isZephyr]);
  return (
    <ModalBackdrop
      onClose={onClose}
      draggable
      dragHandleRef={headerRef}
      contentStyle={{
        background: "white",
        width: "calc(100vw - 40px)",
        maxWidth: 900,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        borderRadius: 12,
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      <div
        ref={headerRef}
        style={{
          background: "#00695C",
          color: "white",
          padding: "14px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          {`Comments — ${formatRef(req.refNo)}`}
          {notes.length > 0 && (
            <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8, opacity: 0.8 }}>
              ({notes.length})
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* "Send Notification" button removed — Add Comment already notifies the shelter. */}
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: 6,
              color: "white",
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer",
              lineHeight: 1,
              padding: "4px 10px",
            }}
          >
            X
          </button>
        </div>
      </div>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${"#CFE0DE"}`,
          flexShrink: 0,
          background: "#FAFAFA",
        }}
      >
        {isZephyr && (
          <div style={{ display: "flex" }}>
            {[
              { key: "comment", label: `${GLYPH.speech} Comment to DFV Service` },
              { key: "internal", label: `${GLYPH.lock} Internal Note` },
            ].map((tab) => {
              const active = mode === tab.key;
              const t = NOTE_THEMES[tab.key];
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={function () {
                    setMode(tab.key);
                  }}
                  style={{
                    flex: 1,
                    background: active ? t.bg : "white",
                    color: active ? t.text : "#546E7A",
                    border: active ? `2px solid ${t.border}` : "1px solid #CFD8DC",
                    borderBottom: active ? "none" : `2px solid ${theme.border}`,
                    borderRadius: "8px 8px 0 0",
                    padding: "9px 12px",
                    fontSize: 14,
                    fontWeight: active ? 700 : 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
        <div
          style={
            isZephyr
              ? {
                  background: theme.bg,
                  border: `2px solid ${theme.border}`,
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: 14,
                }
              : undefined
          }
        >
          <div
            style={{
              fontSize: isZephyr ? 12 : 11,
              fontWeight: 700,
              color: isZephyr ? theme.text : "#607D79",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            {isZephyr
              ? internalMode
                ? "New Internal Note — Zephyr staff only"
                : "New Comment to DFV Service"
              : "New Comment"}
          </div>
          <textarea
            value={note}
            onChange={function (e) {
              setNote(e.target.value);
            }}
            aria-label={internalMode ? "New internal note" : "New comment"}
            placeholder={
              internalMode
                ? "Type your internal note here..."
                : isZephyr
                  ? "Type your comment to the DFV Service..."
                  : "Type your comment here..."
            }
            rows={8}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: isZephyr ? `1px solid ${theme.border}` : `1px solid ${"#CFE0DE"}`,
              background: "white",
              fontSize: 13,
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: 160,
            }}
          />
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {internalMode ? (
              <button
                type="button"
                onClick={addInternalNote}
                disabled={savingInternal}
                style={{
                  background: NOTE_THEMES.internal.border,
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: savingInternal ? "not-allowed" : "pointer",
                  opacity: savingInternal ? 0.6 : 1,
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {`${GLYPH.lock} Add Internal Note`}
              </button>
            ) : (
              <>
                {isZephyr && (
                  <button
                    onClick={function () {
                      setShowTemplatePicker(true);
                    }}
                    style={{
                      background: "none",
                      border: `1px solid ${"#00897B"}`,
                      color: "#00897B",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Template
                  </button>
                )}
                <Btn
                  onClick={async function () {
                    if (!note.trim()) return;
                    const newNote = {
                      author: sanitizeText(user.shelterName),
                      text: sanitizeNote(note),
                      date: new Date().toLocaleString("en-AU"),
                      // Sortable ISO timestamp (the display `date` is a locale string).
                      ts: new Date().toISOString(),
                      // Role of the author, so the shelter's "Latest Update from Zephyr"
                      // can reliably tell staff comments from shelter comments.
                      byRole: isZephyr ? "staff" : "shelter",
                      // Finer tier for the thread colour (responder / supervisor / shelter).
                      tier: authorCommentTier(isZephyr, isAdminRole(user)),
                    };
                    // Atomic server-side append (db-200) — never rewrite the whole notes
                    // array from this browser's copy, which silently dropped notes added by
                    // others since the panel opened (docs/notes-atomic-append-plan.md). A
                    // STAFF comment marks the request unread for the DFV Service (db-77); a
                    // shelter's own does not. updated_by_role is derived server-side.
                    const { notes: mergedNotes, error } = await appendRequestNotes(
                      req.id,
                      [newNote],
                      {
                        updatedBy: user.shelterName,
                        markUnread: isZephyr,
                      }
                    );
                    if (!error) {
                      setNotes(mergedNotes ?? [...notes, newNote]);
                      setNote("");
                      setEmailInvoicing(false);
                      logAudit(
                        "comment_sent",
                        {
                          request_id: req.id,
                          ref: req.refNo,
                          by: user.shelterName,
                        },
                        req.id
                      );
                      if (isZephyr) {
                        // A staff comment is responder "activity" that does NOT auto-clear
                        // the task (only Confirm & Send does). Flag it so the request-detail
                        // exit prompt can offer to mark the task actioned on the way out.
                        if (onActivity) onActivity();
                        if (req.shelterEmail) {
                          sendEmail("comment_to_shelter", {
                            requestId: req.id,
                            refNo: req.refNo,
                            shelterName: req.shelterName,
                            shelterEmail: req.shelterEmail,
                          });
                        }
                      } else {
                        // Route the DFV-Service comment to the correct staff queue: a comment
                        // on a Stock Top-up (request_type) becomes a `top_up_comment` task so it
                        // lands in the Top-up Task List, not the Responders queue (2026-3076).
                        createTask(
                          req.requestType === "stock_top_up" ? "top_up_comment" : "new_comment",
                          {
                            requestId: req.id,
                            refNo: req.refNo,
                            shelterName: req.shelterName,
                            shelterCode: req.shelterCode || "",
                            state: req.state,
                            commentAuthor: user.shelterName,
                          }
                        );
                      }
                      if (emailInvoicing) {
                        sendEmail("comment_to_invoicing", {
                          requestId: req.id,
                          refNo: req.refNo,
                          shelterName: req.shelterName,
                          commentText: newNote.text,
                          commentAuthor: newNote.author,
                          commentDate: newNote.date,
                        });
                      }
                    }
                  }}
                  style={{ padding: "7px 16px", whiteSpace: "nowrap" }}
                >
                  Add Comment
                </Btn>
                {isZephyr && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginLeft: "auto",
                      fontSize: 12,
                      color: "#607D79",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={emailInvoicing}
                      onChange={function (e) {
                        setEmailInvoicing(e.target.checked);
                      }}
                    />
                    Also email invoicing team (invoicing@zephyreducation.com.au)
                  </label>
                )}
              </>
            )}
          </div>
          {isZephyr && (
            <div
              data-testid="compose-explainer"
              style={{ marginTop: 8, fontSize: 12, color: theme.text, lineHeight: 1.5 }}
            >
              {internalMode ? (
                <>
                  <b>The DFV Service will NOT see this note.</b> It is for this request only, and no
                  email is sent.
                </>
              ) : (
                <>
                  <b>The DFV Service will see this comment</b> and is emailed that there is a new
                  comment.
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "12px 20px" }}>
          {thread.length === 0 && (
            <div
              style={{
                color: "#607D79",
                fontSize: 13,
                fontStyle: "italic",
                padding: "8px 0",
              }}
            >
              No comments yet.
            </div>
          )}
          {[...thread].reverse().map(function (n, i) {
            const isInternal = n.kind === "internal";
            // Internal notes render in a distinct grey card with a lock badge, so
            // staff can never mistake one for a shelter-visible comment. Shelter
            // comments tint by the author's tier (responder/supervisor/shelter).
            const nameColour = isInternal ? "#455A64" : n.scheme.name;
            const bgColour = isInternal ? "#ECEFF1" : n.scheme.bg;
            const borderCol = isInternal ? "#B0BEC5" : n.scheme.border;
            return (
              <div
                key={n.key}
                style={{
                  marginBottom: 6,
                  paddingBottom: 6,
                  borderBottom: i < thread.length - 1 ? `1px solid ${"#CFE0DE"}` : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    marginBottom: 3,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: nameColour }}>
                    {n.author}
                  </span>
                  {isInternal && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#B71C1C",
                        background: "#FFEBEE",
                        border: "1px solid #EF9A9A",
                        borderRadius: 4,
                        padding: "1px 6px",
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                      }}
                    >
                      {GLYPH.lock} Internal — hidden from DFV Service
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: "#607D79" }}>{n.date}</span>
                </div>
                <div
                  style={{
                    background: bgColour,
                    border: `1px solid ${borderCol}`,
                    borderRadius: 7,
                    padding: "7px 12px",
                    fontSize: 13,
                    color: "#1A2E2B",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: plainTextDisplayHtml(n.text) }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* end scrollable wrapper */}
    </ModalBackdrop>
  );
}
